// #606 / #625 — opacity for marker-based live layers.
//
// Weather/Smoke/AFF live layers render HTML `mapboxgl.Marker` elements rather
// than mapbox paint layers, so their opacity is applied as CSS. It is applied to
// the marker's inner content child rather than the marker root: mapbox rewrites
// the root element's inline style when it repositions markers on zoom/pan, which
// would drop a root-level opacity back to 1 (#625). The content child is never
// touched by repositioning, so opacity set there survives zoom/pan with no
// re-apply. Markers are still recreated on each data refresh, so providers
// re-apply the current opacity inside their markerFunction.

import type mapboxgl from 'mapbox-gl';

/** Clamp an opacity value to the valid 0..1 range. */
export function clampOpacity(opacity: number): number {
  if (Number.isNaN(opacity)) return 1;
  return Math.max(0, Math.min(1, opacity));
}

/**
 * Apply a CSS opacity to each marker's content child (the single inner element
 * holding the visual — img/svg/icon/barb). Falls back to the marker root when a
 * marker has no child. Null-safe: tolerates a missing list and markers whose
 * element isn't available yet.
 */
export function applyOpacityToMarkers(
  markers: mapboxgl.Marker[] | null | undefined,
  opacity: number,
): void {
  if (!markers || markers.length === 0) return;
  const value = String(clampOpacity(opacity));
  for (const marker of markers) {
    const el = marker?.getElement?.();
    if (!el) continue;
    const target = (el.firstElementChild as HTMLElement | null) ?? el;
    target.style.opacity = value;
  }
}
